import { useState, useMemo } from 'react';
import { LineChart, BarChart, useDrawingArea, useXScale } from '@mui/x-charts';
import { Skeleton } from '@mui/material';
import { IconChartLine, IconChartBar } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { ButtonGroup, Text } from '@fragments';
import { getIntlLocale } from '@i18n';

import {
    useResolvedCssColor,
    formatMoneyCompact,
    formatMoneyFull,
    formatNumberCompact,
    bucketLabel,
    bucketTooltipLabel,
    computeTickLabelInterval,
} from '../helpers';

import '../styles.scss';
import './styles.scss';

// Maps an ISO date to the x-axis label of the bucket that contains it, so a
// caller can anchor a vertical reference line to "the month X happened" without
// knowing the chart's internal label format. Returns null when the date falls
// outside every bucket.
function findBucketLabel(points, labels, isoDate) {
    if (!isoDate) return null;
    const t = new Date(isoDate).getTime();
    if (Number.isNaN(t)) return null;
    for (let i = 0; i < points.length; i++) {
        const fromStr = points[i]?.date?.from;
        if (!fromStr) continue;
        const from = new Date(fromStr.replace(' ', 'T')).getTime();
        const toStr = points[i]?.date?.to;
        if (toStr) {
            if (t >= from && t <= new Date(toStr.replace(' ', 'T')).getTime()) return labels[i];
        } else {
            // No explicit bucket end — fall back to "before the next bucket starts".
            const nextStr = points[i + 1]?.date?.from;
            const next = nextStr ? new Date(nextStr.replace(' ', 'T')).getTime() : Infinity;
            if (t >= from && t < next) return labels[i];
        }
    }
    return null;
}

const VIEW_OPTIONS = [
    { value: 'line', icon: <IconChartLine size={16} /> },
    { value: 'bar', icon: <IconChartBar size={16} /> },
];

// Axis/grid/legend colors live in `_charts/styles.scss` (CSS rules with
// `!important` so they beat MUI's inline styles). The only chart-wide sx
// override we still need here is hiding the legend — single series.
const chartSx = {
    '.MuiChartsLegend-root': {
        display: 'none',
    },
};

// Vertical marker centered on a category, with a floating label chip above the
// plot. Rendered as a child of the chart so it can read the live scale + drawing
// area: `scale(x)` gives the band's left edge, so we add half the bandwidth to
// land on the column's center (bandwidth is 0 for the line chart's point scale,
// so the same math centers there too). The label is real SVG (rect + text +
// drop-shadow) so it can have a background and shadow that bare axis text can't.
const REF_CHIP_HEIGHT = 22;
const REF_CHIP_GAP = 8;
const REF_TOP_MARGIN = 44; // room above the plot for the chip

function ReferenceMarker({ x, label }) {
    const { left, top, width, height } = useDrawingArea();
    const scale = useXScale();

    const base = scale(x);
    if (base === undefined) return null;

    const band = typeof scale.bandwidth === 'function' ? scale.bandwidth() : 0;
    const cx = base + band / 2;

    const chipW = Math.max(56, (label?.length || 0) * 6.2 + 22);
    const half = chipW / 2;
    // Keep the chip inside the drawing area even when the line sits near an edge;
    // the line itself stays on the true center.
    const chipCx = Math.min(Math.max(cx, left + half), left + width - half);
    const chipY = top - REF_CHIP_HEIGHT - REF_CHIP_GAP;

    return (
        <g style={{ pointerEvents: 'none' }}>
            <defs>
                <filter id="ts-ref-shadow" x="-60%" y="-60%" width="220%" height="240%">
                    <feDropShadow dx="0" dy="3" stdDeviation="4" floodColor="rgba(0,0,0,0.35)" floodOpacity="1" />
                </filter>
            </defs>
            <line
                x1={cx}
                x2={cx}
                y1={top}
                y2={top + height}
                style={{ stroke: 'var(--surface-4)', strokeWidth: 1.5, strokeDasharray: '5 4' }}
            />
            <rect
                x={chipCx - half}
                y={chipY}
                width={chipW}
                height={REF_CHIP_HEIGHT}
                rx={7}
                filter="url(#ts-ref-shadow)"
                style={{ fill: 'var(--surface-1)', stroke: 'var(--surface-2)', strokeWidth: 1 }}
            />
            <text
                x={chipCx}
                y={chipY + REF_CHIP_HEIGHT / 2}
                textAnchor="middle"
                dominantBaseline="central"
                style={{ fill: 'var(--surface-8)', fontSize: 11, fontFamily: 'inherit' }}
            >
                {label}
            </text>
        </g>
    );
}

export default function TimeSeriesChart({
    title,
    icon,
    timeSeries,
    valueType = 'number',  // 'number' | 'money'
    currency = 'BRL',
    unitSingular,
    unitPlural,
    report,
    reportLoading,
    defaultMode = 'bar',
    colorVar = '--chart-1',
    // Optional vertical marker: { date: ISOString, label: string }. Anchored to
    // the bucket containing `date`, so it lines up with the axis tick.
    referenceLine,
    // 'small' 16 (default) | 'medium' 20 | 'large' 24 | 'full' pill — the icon
    // chip follows (see .ts-chart-card in styles.scss).
    cornerRadius = 'small',
}) {
    const { t } = useTranslation('dashboard');
    const [mode, setMode] = useState(defaultMode);
    const seriesColor = useResolvedCssColor(colorVar);

    const points = timeSeries?.data || [];
    const granularityType = timeSeries?.granularity?.type || 'day';
    const granularityAmount = timeSeries?.granularity?.amount || 1;

    const isMoney = valueType === 'money';

    const { values, labels, total, tickLabelInterval } = useMemo(() => {
        const raw = points.map(p => Number(p.value) || 0);
        const vals = isMoney ? raw.map(v => v / 100) : raw;
        const hourSpansDays = granularityType === 'hour' && points.length > 24;
        const lbls = points.map(p => bucketLabel(p, granularityType, hourSpansDays));
        const sum = vals.reduce((acc, v) => acc + v, 0);
        const interval = computeTickLabelInterval(points, granularityType);
        return { values: vals, labels: lbls, total: sum, tickLabelInterval: interval };
    }, [points, granularityType, isMoney]);

    const formatShort = isMoney ? (value) => formatMoneyCompact(value, currency) : formatNumberCompact;

    function formatFull(value) {
        if (isMoney) return formatMoneyFull(value, currency);
        const n = Number(value);
        const noun = n === 1
            ? (unitSingular || t('charts.unit.item'))
            : (unitPlural || t('charts.unit.items'));
        return `${n.toLocaleString(getIntlLocale())} ${noun}`;
    }

    const valueFormatter = (v, ctx) => {
        const idx = ctx?.dataIndex;
        const range = idx != null ? bucketTooltipLabel(points[idx], granularityType, granularityAmount) : '';
        const valueText = formatFull(v);
        return range ? `${valueText} · ${range}` : valueText;
    };

    const totalText = formatFull(total);

    const referenceX = useMemo(
        () => (referenceLine?.date ? findBucketLabel(points, labels, referenceLine.date) : null),
        [referenceLine, points, labels]
    );

    const referenceEl = referenceX != null ? (
        <ReferenceMarker x={referenceX} label={referenceLine?.label} />
    ) : null;

    // Reserve headroom for the marker chip only when a reference line is shown,
    // so charts without one keep their tight top margin.
    const topMargin = referenceX != null ? REF_TOP_MARGIN : 16;

    const radiusClass = cornerRadius !== 'small' ? ` ts-chart-card--radius-${cornerRadius}` : '';

    return (
        <div className={`ts-chart-card${radiusClass}${reportLoading && report ? ' ts-chart-card--reloading' : ''}`}>
            <div className="ts-chart-card__header">
                <div className="ts-chart-card__title">
                    <span className="ts-chart-card__icon">{icon}</span>
                    <Text.h4 contrast={7}>{title}</Text.h4>
                    <Text.span contrast={4} className="ts-chart-card__count">
                        · {t('charts.total', { value: totalText })}
                    </Text.span>
                </div>
                <ButtonGroup
                    options={VIEW_OPTIONS}
                    value={mode}
                    onChange={setMode}
                    size="small"
                />
            </div>

            <div className="ts-chart-card__body">
                {reportLoading && !report ? (
                    <Skeleton variant="rounded" height={260} className="ts-chart-card__skeleton" />
                ) : !points.length ? (
                    <div className="ts-chart-card__empty">
                        <Text.p contrast={5}>{t('charts.empty')}</Text.p>
                    </div>
                ) : mode === 'line' ? (
                    <LineChart
                        height={260}
                        skipAnimation={false}
                        series={[{
                            data: values,
                            color: seriesColor,
                            curve: 'monotoneX',
                            area: true,
                            showMark: values.length <= 24,
                            valueFormatter,
                        }]}
                        xAxis={[{ data: labels, scaleType: 'point', tickLabelInterval }]}
                        yAxis={[{ valueFormatter: formatShort }]}
                        grid={{ horizontal: true }}
                        margin={{ top: topMargin, bottom: 28, left: isMoney ? 56 : 36, right: 16 }}
                        sx={{
                            ...chartSx,
                            '.MuiLineElement-root': {
                                strokeWidth: 2,
                            },
                            '.MuiAreaElement-root': {
                                fill: seriesColor,
                                opacity: 0.12,
                            },
                            '.MuiMarkElement-root': {
                                stroke: seriesColor,
                                fill: 'var(--surface-10)',
                                strokeWidth: 2,
                            },
                        }}
                    >
                        {referenceEl}
                    </LineChart>
                ) : (
                    <BarChart
                        height={260}
                        skipAnimation={false}
                        series={[{
                            data: values,
                            color: seriesColor,
                            valueFormatter,
                        }]}
                        xAxis={[{ data: labels, scaleType: 'band', tickLabelInterval }]}
                        yAxis={[{ valueFormatter: formatShort }]}
                        grid={{ horizontal: true }}
                        margin={{ top: topMargin, bottom: 28, left: isMoney ? 56 : 36, right: 16 }}
                        borderRadius={6}
                        slotProps={{ bar: { rx: 4 } }}
                        sx={chartSx}
                    >
                        {referenceEl}
                    </BarChart>
                )}
            </div>
        </div>
    );
}
